import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Typography, Stack, Box } from '@mui/material';
import patternImage from '../images/bg.png'
import canadaMap from '../images/Flag_map_of_Canada.svg';
import usMap from '../images/Flag-map_of_the_United_States.svg';
import { useSiteContent } from '../context/SiteContentContext';
import { COLORS } from '../theme/colors';

const YEARS_TARGET = 25;
const COUNT_DURATION_MS = 1500;

function AboutUs({ aboutText: aboutTextProp }) {
    const { aboutText: contextAboutText } = useSiteContent();
    const aboutText = aboutTextProp ?? contextAboutText;
    const paragraphs = aboutText.split('\n\n');
    const [yearsCount, setYearsCount] = useState(0);
    const countRef = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const node = countRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    const startTime = performance.now();

                    const tick = (now) => {
                        const progress = Math.min((now - startTime) / COUNT_DURATION_MS, 1);
                        setYearsCount(Math.round(progress * YEARS_TARGET));
                        if (progress < 1) {
                            requestAnimationFrame(tick);
                        }
                    };

                    requestAnimationFrame(tick);
                    observer.disconnect();
                }
            },
            { threshold: 0.3 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                boxShadow: ' rgba(0, 0, 0, 0.3) 0px 19px 38px, rgba(0, 0, 0, 0.22) 0px 15px 12px;',
            }}
        >
            <Stack sx={{
                position: 'relative',
                padding: { xs: 3, sm: 5, md: 10 },
                justifyContent: 'center', alignItems: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundImage:
                    'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 16%), transparent)',
                backgroundColor: COLORS.darkSection,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${patternImage})`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto', // adjust as needed
                    opacity: 0.05, // Set your desired transparency (0 is fully transparent, 1 is fully opaque)
                    zIndex: 1, // ensures the pseudo-element stays behind your content
                },

            }}>
               
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: { xs: 4, md: 6 },
                        width: '100%',
                    }}
                >
             
                    <Box sx={{ width: { xs: '100%', md: '60%' }, mt: { xs: 4, md: 0 } }}>
                         <Typography
                    variant="h1"
                    sx={{
                        color: '#fff',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        fontSize: 'clamp(3rem, 10vw, 3.5rem)',
                    }}
                >
                    About&nbsp;Us&nbsp;
                </Typography>
                        {paragraphs.map((paragraph, index) => (
                            <Typography
                                key={index}
                                sx={{
                                    fontWeight: 600,
                                    mt: index === 0 ? 0 : 1,
                                    color: COLORS.accentText,
                                    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                                }}
                            >
                                {paragraph}
                            </Typography>
                        ))}
                    </Box>
                           <Box ref={countRef} sx={{ textAlign: 'center', flexShrink: 0 }}>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                color: '#fff',
                                fontSize: 'clamp(3rem, 12vw, 4rem)',
                                lineHeight: 1,
                            }}
                        >
                            {yearsCount}+
                        </Typography>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                color: '#fff',
                                fontSize: 'clamp(0.8rem, 2.5vw, 1rem)',
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                            }}
                        >
                            Years of Experience
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 3 }}>
                            <Box
                                component="img"
                                src={canadaMap}
                                alt="Canada"
                                sx={{ width: { xs: 40, sm: 56 }, height: { xs: 40, sm: 56 }, objectFit: 'contain' }}
                            />
                            <Box
                                component="img"
                                src={usMap}
                                alt="United States"
                                sx={{ width: { xs: 40, sm: 56 }, height: { xs: 40, sm: 56 }, objectFit: 'contain' }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Stack>
        </Box>
    );
}


export default AboutUs;